/**
 * O fio do zero — como o "Lançar pinout" descreve (Q1): o nome do circuito, a
 * cor, o chicote, o tipo e a obs; depois as pontas, onde ele encosta.
 *
 * Duas entradas. **Do componente** (a caixa vazia de um componente sem pino, ou
 * o topo de qualquer caixa): a primeira ponta é nele, e o nome do fio é digitado.
 * **Do pino** ("Nova ligação a partir deste pino"): o fio é o daquele pino, e só
 * faltam as outras pontas.
 *
 * O nome é conferido **enquanto se digita** (Q13), com a regra do motor: se já
 * existe (a ponta entra nele — Q4), se existe em mais de uma grafia (o operador
 * escolhe — Q12), e o que ele puxa pelo começo (o operador confirma — Q5). O que
 * a conferência do lote vai recusar aparece aqui antes de ir para o lote.
 *
 * Nada é gravado aqui: o botão põe uma operação `criarPino` por ponta no lote.
 */

import { useEffect, useMemo, useState } from "react"
import { usarChicotes, usarConectores, usarCores } from "@/api/catalogos"
import {
  usarBuscaDeComponentes, usarConferenciaDoNome,
  type ConferenciaDoNome, type NoDoGrafo, type OperacaoDoEditor,
} from "@/api/editorDeLigacoes"
import { CampoDeBusca, idDe, textoDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"
import { TIPOS_COMPONENTE, TIPOS_PINOUT } from "../../tipos"
import { faltasDoFio, operacoesDoFio, precisaConfirmar, type Escolha, type Fio, type PontaDoFio } from "./regras"

export type ModoDoFio =
  /** O fio sai deste componente: a primeira ponta é nele. */
  | { tipo: "componente"; componenteId: number; codigo: string }
  /** O fio é o deste pino; faltam as outras pontas. */
  | { tipo: "pino"; no: NoDoGrafo }

export type PontaNaTela = { chave: number; componente: ValorBusca; tipoComponente: number; conector: ValorBusca; pino: string }

const escolha = (v: ValorBusca): Escolha => ({ id: idDe(v), texto: textoDe(v) })
export const pontaVazia = (chave: number): PontaNaTela => ({ chave, componente: VAZIO, tipoComponente: 0, conector: VAZIO, pino: "" })

export function FormularioDoFio({ projetoId, modo, restricaoId, restricaoRotulo, fonte, aoPorNoLote, aoCancelar }: {
  projetoId: number
  modo: ModoDoFio
  restricaoId: number | null
  restricaoRotulo: string
  fonte: string
  aoPorNoLote: (itens: { op: OperacaoDoEditor; texto: string }[]) => void
  aoCancelar: () => void
}) {
  const doPino = modo.tipo === "pino" ? modo.no : null
  const cores = usarCores(projetoId)
  const chicotes = usarChicotes(projetoId)

  const [nome, setNome] = useState("")
  const [grafia, setGrafia] = useState<number | null>(null)
  const [confirmado, setConfirmado] = useState(false)
  const [cor, setCor] = useState<ValorBusca>(() => (doPino?.cor.trim() ? { estado: "novo", texto: doPino.cor.trim() } : VAZIO))
  const [corMexida, setCorMexida] = useState(false)
  const [chicote, setChicote] = useState<ValorBusca>(VAZIO)
  const [tipoPino, setTipoPino] = useState(0)
  const [obs, setObs] = useState("")
  const [pontas, setPontas] = useState<PontaNaTela[]>(() =>
    modo.tipo === "componente"
      ? [{ ...pontaVazia(1), componente: { estado: "vinculado", id: modo.componenteId, texto: modo.codigo } }]
      : [pontaVazia(1)])

  const { conferencia, carregando } = usarConferenciaDoNome(projetoId, doPino ? "" : nome)

  // Outro nome é outra conferência: a escolha de grafia e a confirmação eram do nome anterior.
  useEffect(() => { setConfirmado(false); setGrafia(null) }, [nome])

  // A cor habitual do fio que já existe entra sozinha (Q7) — enquanto ninguém mexeu na cor.
  const escolhida = conferencia?.grafias.length === 1 ? conferencia.grafias[0] : conferencia?.grafias.find((g) => g.circuitoId === grafia)
  useEffect(() => {
    const habitual = escolhida?.corHabitual?.trim()
    if (corMexida || !habitual) return
    const daLista = cores.itens.find((c) => c.rotulo.trim().toLowerCase() === habitual.toLowerCase())
    setCor(daLista ? { estado: "vinculado", id: daLista.id, texto: daLista.rotulo } : { estado: "novo", texto: habitual })
  }, [escolhida?.circuitoId, escolhida?.corHabitual, corMexida, cores.itens.length])

  const circuito: Escolha = doPino
    ? { id: doPino.circuitoId, texto: doPino.circuito }
    : grafia && escolhida ? { id: escolhida.circuitoId, texto: escolhida.descricao } : { id: 0, texto: nome }
  const nomeDoFio = doPino?.circuito ?? escolhida?.descricao ?? nome.trim()

  const fio: Fio = {
    circuito, cor: escolha(cor), chicote: escolha(chicote), tipoPino, obs, fonte, restricaoId,
  }
  const pontasEscolhidas: PontaDoFio[] = pontas.map((p) => ({
    componente: escolha(p.componente), tipoComponente: p.tipoComponente, conector: escolha(p.conector), pino: p.pino,
  }))
  const faltas = faltasDoFio(fio, pontasEscolhidas, { conferencia: doPino ? null : conferencia, confirmado })
  const umaPontaSo = !doPino && pontas.length === 1 && !(escolhida && escolhida.pinos > 0)

  function mudarPonta(chave: number, mudar: (p: PontaNaTela) => PontaNaTela) {
    setPontas((l) => l.map((p) => (p.chave === chave ? mudar(p) : p)))
  }

  return (
    <section aria-label="Novo fio" style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div>
        <b style={{ fontSize: 13 }}>{doPino ? "Nova ligação a partir deste pino" : `Novo fio saindo de ${modo.tipo === "componente" ? modo.codigo : ""}`}</b>
        <div style={{ fontSize: 12, color: "var(--txt2)" }}>
          {doPino
            ? <>O fio <code style={{ fontFamily: "var(--fm)" }}>{doPino.circuito || "(vazio)"}</code> sai de {doPino.componenteCodigo || doPino.componente} {doPino.conector}·{doPino.pino} e chega em:</>
            : "Dê o nome do fio e diga onde ele encosta. Nada é gravado até o Gravar do lote."}
        </div>
      </div>

      {!doPino && (
        <>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={rotulo}>Nome do fio (circuito)</span>
            <input
              className="campo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="ex.: E46T36.E1"
              aria-label="Nome do fio"
              style={{ ...campo, fontFamily: "var(--fm)" }}
            />
          </label>
          {nome.trim() !== "" && (
            <PainelDoNome
              conferencia={conferencia}
              carregando={carregando}
              grafia={grafia}
              aoEscolherGrafia={setGrafia}
              confirmado={confirmado}
              aoConfirmar={setConfirmado}
            />
          )}
        </>
      )}

      {pontas.map((p, i) => (
        <CampoDaPonta
          key={p.chave}
          projetoId={projetoId}
          ponta={p}
          titulo={pontas.length > 1 || doPino ? `Ponta ${i + 1}` : "Onde o fio sai"}
          fixo={modo.tipo === "componente" && i === 0}
          podeTirar={pontas.length > 1 && !(modo.tipo === "componente" && i === 0)}
          aoMudar={(m) => mudarPonta(p.chave, m)}
          aoTirar={() => setPontas((l) => l.filter((x) => x.chave !== p.chave))}
        />
      ))}
      <button
        type="button"
        onClick={() => setPontas((l) => [...l, pontaVazia(Math.max(0, ...l.map((x) => x.chave)) + 1)])}
        style={{ alignSelf: "flex-start", height: 28, padding: "0 10px", borderRadius: 7, fontSize: 12, border: "1px dashed var(--line2)", background: "var(--surface)", color: "var(--txt2)", cursor: "pointer" }}
      >
        + mais uma ponta
      </button>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <CampoDeBusca
          rotulo="Cor"
          valor={cor}
          aoMudar={(v) => { setCor(v); setCorMexida(true) }}
          fonte={{ modo: "memoria", itens: cores.itens, carregando: cores.carregando }}
          id="fio-cor"
        />
        <CampoDeBusca
          rotulo="Chicote"
          valor={chicote}
          aoMudar={setChicote}
          fonte={{ modo: "memoria", itens: chicotes.itens, carregando: chicotes.carregando }}
          id="fio-chicote"
        />
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={rotulo}>Tipo do pino</span>
          <select className="campo" value={tipoPino} onChange={(e) => setTipoPino(Number(e.target.value))} style={campo} aria-label="Tipo do pino">
            {Object.entries(TIPOS_PINOUT).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={rotulo}>Obs</span>
          <input className="campo" value={obs} onChange={(e) => setObs(e.target.value)} style={campo} aria-label="Obs" />
        </label>
      </div>

      <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
        Restrição: <b>{restricaoId === null ? "nenhuma escolhida" : restricaoId === 0 ? "Todos os modelos" : restricaoRotulo}</b>
        {" · "}Fonte: <b>{fonte.trim() || "—"}</b> (as duas no cabeçalho do editor)
      </span>
      {umaPontaSo && faltas.length === 0 && (
        <span style={{ fontSize: 11.5, color: "var(--aviso-on-soft)" }}>
          O fio vai ter uma ponta só por enquanto — dá para gravar e ligar a outra depois.
        </span>
      )}
      {faltas.length > 0 && <span style={{ fontSize: 11.5, color: "var(--acao-on-soft)" }}>Falta: {faltas.join("; ")}.</span>}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          disabled={faltas.length > 0}
          onClick={() => aoPorNoLote(operacoesDoFio(fio, pontasEscolhidas, nomeDoFio, (t) => TIPOS_COMPONENTE[t] ?? "Normal"))}
          style={{ ...botao, flex: 1, border: "none", background: "var(--brand)", color: "#FFFFFF", opacity: faltas.length > 0 ? 0.5 : 1 }}
        >
          Pôr no lote ({pontas.length} ponta{pontas.length > 1 ? "s" : ""})
        </button>
        <button type="button" onClick={aoCancelar} style={{ ...botao, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--txt2)" }}>
          Cancelar
        </button>
      </div>
    </section>
  )
}

function PainelDoNome({ conferencia, carregando, grafia, aoEscolherGrafia, confirmado, aoConfirmar }: {
  conferencia: ConferenciaDoNome | null
  carregando: boolean
  grafia: number | null
  aoEscolherGrafia: (id: number) => void
  confirmado: boolean
  aoConfirmar: (v: boolean) => void
}) {
  if (!conferencia) return <span style={{ fontSize: 12, color: "var(--txt3)" }}>{carregando ? "Conferindo o nome…" : ""}</span>
  const liga = [...conferencia.alcanca, ...conferencia.alcancadoPor.filter((a) => !conferencia.alcanca.some((b) => b.circuitoId === a.circuitoId))]
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 9, borderRadius: 7, background: "var(--surface2)", fontSize: 12 }}>
      {conferencia.recusa && (
        <div role="alert" style={{ padding: 8, borderRadius: 7, background: "var(--erro-soft)", color: "var(--erro-on-soft)" }}>
          {conferencia.recusa}
        </div>
      )}
      {conferencia.grafias.length === 1 && (
        <span>
          <b>Este fio já existe</b>: {conferencia.grafias[0]!.pinos} ponta(s)
          {conferencia.grafias[0]!.onde.length > 0 ? ` em ${conferencia.grafias[0]!.onde.join(", ")}` : ""}. A ponta nova entra nele.
        </span>
      )}
      {conferencia.grafias.length > 1 && (
        <div role="radiogroup" aria-label="Qual grafia é o fio" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <b>Esse nome existe em {conferencia.grafias.length} grafias. Qual é o fio?</b>
          {conferencia.grafias.map((g) => (
            <label key={g.circuitoId} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
              <input type="radio" name="grafia" checked={grafia === g.circuitoId} onChange={() => aoEscolherGrafia(g.circuitoId)} />
              <span><code style={{ fontFamily: "var(--fm)" }}>'{g.descricao}'</code> — {g.pinos} ponta(s){g.onde.length > 0 ? ` em ${g.onde.join(", ")}` : ""}</span>
            </label>
          ))}
        </div>
      )}
      {conferencia.grafias.length === 0 && !conferencia.recusa && <span><b>Fio novo.</b></span>}
      {liga.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span>
            {conferencia.grafias.length === 0 ? "Pelo começo do nome, o desenho também vai ligar este fio em:" : "Pelo começo do nome, este fio também liga em:"}
          </span>
          {liga.slice(0, 12).map((c) => (
            <span key={c.circuitoId} style={{ color: c.curinga ? "var(--erro)" : "var(--txt2)" }}>
              • <code style={{ fontFamily: "var(--fm)" }}>{c.descricao}</code> ({c.pinos} ponta{c.pinos === 1 ? "" : "s"}{c.onde.length > 0 ? `: ${c.onde.join(", ")}` : ""}){c.curinga ? " — só por curinga (_)" : ""}
            </span>
          ))}
          {liga.length > 12 && <span style={{ color: "var(--txt3)" }}>… e mais {liga.length - 12}.</span>}
          {precisaConfirmar(conferencia) && !conferencia.recusa && (
            <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 3 }}>
              <input type="checkbox" checked={confirmado} onChange={(e) => aoConfirmar(e.target.checked)} />
              <b>É isso mesmo: o fio novo também liga nesses circuitos.</b>
            </label>
          )}
        </div>
      )}
    </div>
  )
}

/** Uma ponta: componente (buscado no servidor, ou novo com tipo), conector e pino. */
export function CampoDaPonta({ projetoId, ponta, titulo, fixo, podeTirar, aoMudar, aoTirar }: {
  projetoId: number
  ponta: PontaNaTela
  titulo: string
  fixo: boolean
  podeTirar: boolean
  aoMudar: (m: (p: PontaNaTela) => PontaNaTela) => void
  aoTirar: () => void
}) {
  const busca = usarBuscaDeComponentes(projetoId)
  const componenteId = idDe(ponta.componente)
  // Componente novo ainda não tem conector: sem id, nada de lista (e nada de pedir os do projeto inteiro).
  const conectores = usarConectores(componenteId > 0 ? projetoId : 0, componenteId || undefined)
  const novo = ponta.componente.estado === "novo"
  const ids = useMemo(() => `fio-ponta-${ponta.chave}`, [ponta.chave])

  return (
    <fieldset style={{ border: "1px solid var(--line)", borderRadius: 7, padding: "7px 9px 9px", margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      <legend style={{ fontSize: 11.5, fontWeight: 700, color: "var(--txt2)", padding: "0 4px" }}>
        {titulo}
        {podeTirar && (
          <button type="button" onClick={aoTirar} title="Tirar esta ponta"
                  style={{ marginLeft: 6, border: "none", background: "none", color: "var(--txt3)", cursor: "pointer" }}>×</button>
        )}
      </legend>
      {fixo ? (
        <span style={{ fontSize: 12 }}>Componente <b>{textoDe(ponta.componente)}</b></span>
      ) : (
        <CampoDeBusca
          rotulo="Componente"
          valor={ponta.componente}
          aoMudar={(v) => aoMudar((p) => ({ ...p, componente: v, conector: VAZIO }))}
          fonte={{ modo: "servidor", itens: busca.itens, carregando: busca.carregando, aoBuscar: busca.buscar }}
          mono
          dica="código ou descrição"
          id={`${ids}-componente`}
        />
      )}
      {novo && (
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={rotulo}>Tipo do componente novo</span>
          <select
            className="campo"
            value={ponta.tipoComponente}
            onChange={(e) => aoMudar((p) => ({ ...p, tipoComponente: Number(e.target.value) }))}
            aria-label="Tipo do componente novo"
            style={campo}
          >
            {Object.entries(TIPOS_COMPONENTE).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
          </select>
        </label>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 6 }}>
        <CampoDeBusca
          rotulo="Conector"
          valor={ponta.conector}
          aoMudar={(v) => aoMudar((p) => ({ ...p, conector: v }))}
          fonte={{ modo: "memoria", itens: conectores.itens, carregando: componenteId > 0 && conectores.carregando }}
          mono
          dica="descrição"
          id={`${ids}-conector`}
        />
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={rotulo}>Pino</span>
          <input
            className="campo"
            value={ponta.pino}
            onChange={(e) => aoMudar((p) => ({ ...p, pino: e.target.value }))}
            aria-label={`${titulo}: pino`}
            style={{ ...campo, fontFamily: "var(--fm)" }}
          />
        </label>
      </div>
    </fieldset>
  )
}

const rotulo: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }
const campo: React.CSSProperties = { height: 30, padding: "0 9px", borderRadius: 7, border: "1px solid var(--line2)", fontSize: 12.5, background: "var(--surface)" }
const botao: React.CSSProperties = { minHeight: 32, padding: "6px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }
